// ============================================================
// AI利用状況 API クライアント (利用状況ダッシュボード / 請求一覧)
// 自BE (client-setting-backend) の /api/v1/ai-usage/* を呼ぶ。
// レスポンスの正本は物件管理側 DynamoDB の集計テーブル (日次/月次バッチで更新)。
// ============================================================

import ipponApi from "../../../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;

// ===== API レスポンス型 (client-setting-backend と一致) =====

/** 年一括前払い（席）の内訳1グループ分（初期契約 / 途中追加、残り月数ごとにまとめる） */
export interface AnnualPrepaySeatBreakdownItem {
  /** 表示ラベル ("初期契約" / "追加アカウント") */
  label: string;
  /** 初期契約分の席か（false=契約途中で追加された席） */
  isInitial: boolean;
  /** このグループに属する席数 */
  seatCount: number;
  /** 席単価 */
  unitPrice: number;
  /** 前払い対象月数（課金開始月〜契約終了月） */
  months: number;
  /** 金額 = seatCount × unitPrice × months */
  amountJpy: number;
}

export interface AiUsageModelRow {
  use: string;
  model: string;
  in: number;
  cache: number;
  out: number;
  pages?: number;
  credits: number;
  costJpy: number;
}

export interface AiUsageSummary {
  estRunCnt: number;
  chatCnt: number;
  totIn: number;
  totCa: number;
  totOut: number;
  totCredits: number;
  pages: number;
  bpoBilledJpy: number;
  agentBilledJpy: number;
  /** アカウント契約請求額（月次の基本料金 + 追加席。年払いは月次0で前払いへ） */
  accountBilledJpy: number;
  /** 年払い一括（基本料金×12）。年払い契約の開始月のみ計上、それ以外は0 */
  annualPrepayBilledJpy?: number;
  /** 上記のうち席分の内訳（初期契約分/途中追加分を分けて表示）。当月発生分が無ければ空配列 */
  annualPrepaySeatBreakdown?: AnnualPrepaySeatBreakdownItem[];
  /** 上記のうちオプション基本料金（AI-BPO）の一括前払い分。当月発生分が無ければ0 */
  annualPrepayOptionJpy?: number;
  /** annualPrepayOptionJpy の前払い対象月数（申込月〜契約終了月） */
  annualPrepayOptionMonths?: number;
  /** 物件登録数請求額（1棟 ¥3,300 の従量 = 登録棟数 × ¥3,300） */
  propertyBilledJpy: number;
  /** 任意請求項目（初期導入費等・DynamoDB billing-custom-items）の当月合計・税込（割引はマイナス可） */
  customBilledJpy?: number;
  /** 任意請求項目の内訳（表示名・税込金額） */
  customItems?: { label: string; amountJpy: number }[];
  /** 合計請求額（年払い一括 + アカウント契約 + AI-BPO + AIエージェント + 物件登録数 + 任意項目）・税込 */
  billedJpy: number;
  /** うち税抜（内税控除） */
  netJpy?: number;
  /** うち消費税（内税） */
  taxJpy?: number;
  bpoIncludedRuns: number;
  bpoBaseJpy: number;
  /** 超過1回あたり単価（マスター/クライアント別上書きを解決した実値） */
  bpoPerRunJpy?: number;
  /** AI-BPO オプション契約中か。false = 未契約 → カード/円グラフ/推移をトルツメ */
  bpoOptionActive: boolean;
  /** 物件登録数（集計時点スナップショット） */
  propertyCount: number;
  userCount: number;
  creditPerUser: number;
  freeAgentCredits: number;
  remainBpoRuns: number;
  remainAgentCredits: number;
  /** 実際に使用した席単価（client_seat_prices 解決値） */
  seatUnitPriceJpy?: number;
  /** 実際に使用した物件登録数単価（マスター/クライアント別上書きを解決した実値） */
  propertyUnitPriceJpy?: number;
  /** 実際に使用したクレジット単価（マスター/クライアント別上書きを解決した実値） */
  creditUnitPriceJpy?: number;
  fxRate: number;
}

export interface DailyUsageResponse {
  year: number;
  month: number;
  days: number;
  labels: string[];
  estCount: number[];
  chatCount: number[];
  est_az_in: number[];
  est_az_ca: number[];
  est_az_out: number[];
  est_di_pg: number[];
  ch_ag_in: number[];
  ch_ag_ca: number[];
  ch_ag_out: number[];
  ch_wh_in: number[];
  ch_wh_out: number[];
  /** チャット (IPPON) の日次使用クレジット */
  ch_credits: number[];
  summary: AiUsageSummary;
  models: AiUsageModelRow[];
  aggregatedAt: string | null;
}

export interface BillingMonthItem {
  year: number;
  month: number;
  estRunCnt: number;
  chatCnt: number;
  tIn: number;
  tCa: number;
  tOut: number;
  credits: number;
  pages: number;
  bpoBilledJpy: number;
  /** その月に AI-BPO オプション契約中か。false = 未契約 → 請求一覧グラフでトルツメ */
  bpoContracted: boolean;
  agentBilledJpy: number;
  /** アカウント契約請求額（月払いの月次基本料金。年払いクライアントは 0） */
  accountBilledJpy: number;
  /** 年払い一括（基本料金×12）。年払いクライアントの契約開始月のみ計上、それ以外は 0 */
  annualPrepayBilledJpy: number;
  /** 物件登録数請求額（10棟まで¥33,000 / 20棟まで¥66,000 / 21棟〜¥99,000） */
  propertyBilledJpy: number;
  /** 任意請求項目（初期導入費・個別対応費 等）の合計。DynamoDB billing-custom-items から取得・該当月のみ */
  customBilledJpy: number;
  /** 任意請求項目の内訳（表示名・税込金額）。割引はマイナス可 */
  customItems: { label: string; amountJpy: number }[];
  /** 合計請求額（年払い一括 + アカウント契約 + AI-BPO + AIエージェント + 物件登録数 + 任意項目）・税込 */
  totalBilledJpy: number;
  /** うち税抜（内税控除） */
  netJpy?: number;
  /** うち消費税（内税） */
  taxJpy?: number;
}

export interface BillingResponse {
  items: BillingMonthItem[];
  nextBefore: string | null;
}

// ===== API 呼び出し =====

export async function fetchDailyUsageApi(
  year: number,
  month: number
): Promise<DailyUsageResponse> {
  const res = await ipponApi.get(`${BaseURL}/api/v1/ai-usage/daily`, {
    params: { year, month },
  });
  return res.data.data as DailyUsageResponse;
}

export async function fetchBillingApi(
  months = 12,
  before?: string
): Promise<BillingResponse> {
  const res = await ipponApi.get(`${BaseURL}/api/v1/ai-usage/billing`, {
    params: { months, ...(before ? { before } : {}) },
  });
  return res.data.data as BillingResponse;
}

// ===== 日付ヘルパー =====

/** 現在月から遡って n ヶ月分の {year, month} を返す */
export function recentMonths(
  curYear: number,
  curMonth: number,
  count: number
): { year: number; month: number }[] {
  const months: { year: number; month: number }[] = [];
  let y = curYear;
  let m = curMonth;
  for (let k = 0; k < count; k++) {
    months.push({ year: y, month: m });
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }
  return months;
}
